/******* Angular Resourse *******/
import { ElementRef, Inject, Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { HttpEvent, HttpEventType } from '@angular/common/http';

/******* Const Resourse *******/
import { ENCK, PLATFORM_CONFIGS, SIGNAL_CAP } from '@core/constant';
import {
    DeviceAlertType,
    DistanceUnit,
    SignalDisplayObject,
    TimeZone,
    Coordinate,
    NotificationType,
    ConfigStatusObject,
    RolePermission,
    FileTransferingProgress,
    FileStage,
    SystemPlatform,
    PlatformConfig
} from '@core/types';

/******* Plug-In Resourse *******/
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import aesjs from 'aes-js';
import { DateTime } from 'luxon';
import * as _ from 'lodash';

/******* Environments Resourse *******/
import { environment } from 'environments/environment';

// Constants
const LABEL_OFFSET = 50;
const SEGMENT_SIZE = 16;
const MAC_ADDRESS_REGEX = /(^0004ED[0-9A-F]{6}$|^600347[0-9A-F]{6}$)/;
const URL_REGEX = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
const FTP_REGEX = /^(ftp|sftp):\/\/([^:@\s]+:[^:@\s]+@)?([\w.-]+)(:\d+)?(\/[\w.-]*)*\/?$/;
const IPV4_REGEX = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const GPS_FORMAT_REGEX = /^[NEWS][0-9]+[.][0-9]+$/;
const FIRMWARE_HEADER = '32524448';
const ROM_FILE_HEADER = '<ROMFILE>';
const CONFIGURATION_HEADER = '3C524F4D46494C453E';

@Injectable({
    providedIn: 'root',
})
export class UtilsService {

    /**
     * Constructor
     */
    constructor(
        private _toastrService: ToastrService,
        @Inject(DOCUMENT) private _document: Document
    ) { }

    /**
     * ACCESSORS
     */

    /**
     * Get the sweet alert template
     */
    get swalMixin(): typeof Swal {
        return Swal.mixin({
            showCancelButton: true,
            showClass: { popup: 'animate__animated animate__bounceIn' },
            customClass: {
                container: 'max-z-index',
                title: 'mt-2 mb-2',
                content: 'loading-display-none',
                confirmButton: 'btn btn-primary loading-border-transparent',
                cancelButton: 'btn btn-secondary loading-display-none',
                input: "form-control"
            },
            allowOutsideClick: true,
            allowEscapeKey: true
        });
    }

    /**
     * Get Platform
     */
    get getPlatform(): SystemPlatform {
        return environment.platform || SystemPlatform.STANDARD;
    }

    /**
     * Get platform Config
     */
    get platformConfig(): PlatformConfig {
        return PLATFORM_CONFIGS[this.getPlatform];
    }

    /**
     * PRIVATE METHODS
     */

    /**
     * Convert radians to degrees
     */
    private radiansToDegrees(radians: number): number {
        const degree = radians % (2 * Math.PI);
        return degree * 180 / Math.PI;
    }

    /**
     * Get top offset of HTML element
     */
    private getTopOffset(controlEl: HTMLElement): number {
        return controlEl.getBoundingClientRect().top + window.scrollY - LABEL_OFFSET;
    }

    /**
     * PRIVATE METHODS - ENCRYPTION & FILE VALIDATION
     */

    /**
     * Decode encrypted configuration
     */
    private decodeConfig(header: string): string {
        try {
            const batch = atob(ENCK).split('|');
            const key = batch[1].split(',').map(x => parseInt(x));
            const iv = batch[0].split(',').map(x => parseInt(x));

            const encryptedBytes = aesjs.utils.hex.toBytes(header);
            const aesCfb = new aesjs.ModeOfOperation.cfb(key, iv, SEGMENT_SIZE);
            const decryptedBytes = aesCfb.decrypt(encryptedBytes);
            const decryptedText = aesjs.utils.utf8.fromBytes(decryptedBytes);

            return atob(decryptedText);
        } catch (error) {
            console.error('Decryption failed:', error);
            return '';
        }
    }

    /**
     * Check SCE file format
     */
    private encodeFileCheck(fileType: 'firmware' | 'configuration', header: string): string {
        switch (fileType) {
            case 'firmware':
                return header.toUpperCase().startsWith(FIRMWARE_HEADER) ? 'afw' : 'unknown';
            case 'configuration':
                return this.decodeConfig(header).toUpperCase().startsWith(ROM_FILE_HEADER) ? 'cfg' : 'unknown';
        }
    }

    /**
     * Check standard file format
     */
    private standardFileCheck(fileType: 'firmware' | 'configuration', header: string, fileNameExt: string): string {
        switch (fileType) {
            case 'firmware':
                return fileNameExt;
            case 'configuration':
                return fileNameExt !== 'cfg' ? fileNameExt : (header.toUpperCase().startsWith(CONFIGURATION_HEADER) ? 'cfg' : 'unknown');
        }
    }

    /**
     * PUBLIC METHODS
     */

    /**
     * Create signal display object
     */
    public createSignalDisplayObject(
        signalTag: string,
        strength: number,
        thresholds: any = null
    ): SignalDisplayObject {
        if (!strength) {
            return {
                signalTag,
                color: '#b1b1b1',
                level: 0,
                strength: "--",
                comment: "not available"
            };
        }

        thresholds = thresholds || SIGNAL_CAP[signalTag].thresholds;
        let levelCount = signalTag.substring(0, 4) === "RSRQ" ? 1 : 0;

        for (const range of thresholds) {
            levelCount++;
            if (strength >= range.larger && strength <= range.less) {
                return {
                    signalTag,
                    color: range.color,
                    level: levelCount,
                    strength: Math.round(strength).toString(),
                    comment: range.comment
                };
            }
        }

        return {
            signalTag,
            color: '#b1b1b1',
            level: 0,
            strength: "--",
            comment: "not available"
        };
    }

    /**
     * Automatically Download the File
     * 
     * @param data 
     * @param {string} dataType 
     * @param {string} fileName 
     */
    public downloadFile(data: any, dataType: string, fileName: string): void {
        const blob = new Blob([data], { type: dataType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = fileName;
        link.target = "_blank";
        link.click();

        URL.revokeObjectURL(url);
    }

    /**
     * Download file from link
     */
    public downloadFileLink(link: string): void {
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.target = "_blank";
        anchor.click();
    }

    /**
     * Convert blob to file
     */
    public blobToFile(blob: Blob, fileName: string): File {
        const file = blob as any;
        file.lastModifiedDate = new Date();
        file.name = fileName;
        return file as File;
    }

    /**
     * Calculate distance between two coordinates
     */
    public calculateDistance(
        locationStart: Coordinate,
        locationEnd: Coordinate,
        unit: DistanceUnit = DistanceUnit.MILES
    ): number {
        const rad = Math.PI / 180;
        const radlat1 = locationStart.Latitude * rad;
        const radlat2 = locationEnd.Latitude * rad;
        const radtheta = (locationStart.Longitude - locationEnd.Longitude) * rad;

        let dist = Math.sin(radlat1) * Math.sin(radlat2) +
            Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

        dist = dist > 1 ? 1 : dist;
        dist = Math.acos(dist) * 180 / Math.PI * 60 * 1.1515;

        if (unit === DistanceUnit.KILOMETERS) dist *= 1.609344;
        if (unit === DistanceUnit.NAUTICAL_MILES) dist *= 0.8684;

        return Math.round(dist * 100) / 100;
    }

    /**
     * Calculate azimuth between two coordinates
     */
    public calculateAzimuth(locationStart: Coordinate, locationEnd: Coordinate): number {
        const rad = Math.PI / 180;
        const lat1 = locationStart.Latitude * rad;
        const lat2 = locationEnd.Latitude * rad;
        const lon1 = locationStart.Longitude * rad;
        const lon2 = locationEnd.Longitude * rad;

        const a = Math.sin(lon2 - lon1) * Math.cos(lat2);
        const b = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

        const azimuth = Math.round(this.radiansToDegrees(Math.atan2(a, b)) * 100) / 100;
        return azimuth > 0 ? azimuth : 360.0 + azimuth;
    }

    /**
     * Create array filled with value
     */
    public initialArray(length: number, value: number | string): Array<number | string> {
        return Array(length).fill(value);
    }

    /**
     * Scroll to first invalid form control
     */
    public scrollToInvalid(element: ElementRef): void {
        const firstInvalidControl: HTMLElement = element.nativeElement.querySelector(
            "form input.ng-invalid, form select.ng-invalid, form input.is-invalid, form select.is-invalid"
        );

        if (firstInvalidControl) {
            firstInvalidControl.focus();
            window.scroll({
                top: this.getTopOffset(firstInvalidControl),
                left: 0,
                behavior: "smooth"
            });
        }
    }

    /**
     * Scroll element to top smoothly
     */
    public scrollToTop(element: Element): void {
        if (!element) return;

        const smoothScroll = () => {
            const currentScroll = element.scrollTop;
            if (currentScroll > 20) {
                window.requestAnimationFrame(smoothScroll);
                element.scrollTop = currentScroll - currentScroll / 4;
            } else {
                element.scrollTop = 0;
            }
        };

        smoothScroll();
    }

    /**
     * Convert number to two-digit string
     */
    public toTwoDigit(num: number): string {
        return num.toString().padStart(2, '0');
    }

    /**
     * Trigger global notification
     */
    public alert(alertType: NotificationType, title: string, content: string): void {
        this._toastrService[alertType](content, title, {
            timeOut: 3000,
            toastClass: 'toast ngx-toastr max-z-index',
            closeButton: false
        });
    }

    /**
    * Check if string is valid JSON
    */
    public isJsonString(testString: string): object {
        try {
            return JSON.parse(testString);
        } catch (e) {
            return {};
        }
    }

    /**
     * Convert configuration status to string
     */
    public toConfigStatus(obj: ConfigStatusObject): string {
        if (obj.isProgress) return "clock";
        if (obj.pending && Object.keys(obj.pending).length > 0) return "clock";
        if (!obj.isProgress && obj.isSynced) return "synced";
        if (!obj.isProgress && !obj.isSynced) return "not synced";
    }

    /**
     * Convert string to boolean
     */
    public stringToBooleanConverter(input: string): boolean {
        return input?.toUpperCase() === "YES";
    }

    /**
     * Convert number string to boolean
     */
    public numberToBooleanConverter(input: string): boolean {
        return input === '1';
    }

    /**
     * Convert boolean to string/number
     */
    public booleanReverseConverter(input: boolean, type: 'string' | 'number'): string {
        return type === 'string' ? (input ? "Yes" : "No") : (input ? "1" : "0");
    }

    /**
     * Enable/disable form controls
     */
    public disableContent(
        controls: Array<UntypedFormControl | UntypedFormGroup | UntypedFormArray>,
        disabled: boolean
    ): void {
        controls.forEach(control => {
            disabled ? control.disable() : control.enable();
        });
    }

    /**
     * Patch form values and mark dirty
     */
    public patchValues(group: UntypedFormGroup, patchData: any, markAsDirty: boolean): void {
        group.patchValue(patchData);

        if (markAsDirty) {
            Object.keys(patchData).forEach(key => {
                group.get(key)?.markAsDirty();
            });
        }
    }

    /**
     * Translate GPS coordinates
     */
    public translateGpsCoordanate(latitude: string, longitude: string): Array<number> {
        if (GPS_FORMAT_REGEX.test(latitude) && GPS_FORMAT_REGEX.test(longitude)) {
            return [
                this.translateGpsLatitude(latitude),
                this.translateGpsLongitude(longitude)
            ];
        }
        return [0, 0];
    }

    /**
     * Translate GPS latitude
     */
    public translateGpsLatitude(latitude: string): number {
        if (!GPS_FORMAT_REGEX.test(latitude)) return 0;

        const sign = latitude[0] === "N" ? 1 : -1;
        const degrees = parseFloat(latitude.substring(1, 3));
        const minutes = parseFloat(latitude.substring(3));
        const result = sign * (degrees + minutes / 60);

        return Math.round(result * 1000000) / 1000000;
    }

    /**
     * Translate GPS longitude
     */
    public translateGpsLongitude(longitude: string): number {
        if (!GPS_FORMAT_REGEX.test(longitude)) return 0;

        const sign = longitude[0] === "E" ? 1 : -1;
        const degrees = parseFloat(longitude.substring(1, 4));
        const minutes = parseFloat(longitude.substring(4));
        const result = sign * (degrees + minutes / 60);

        return Math.round(result * 1000000) / 1000000
    }

    /**
     * Convert uptime to readable string
     */
    public toTimeString(dataSource: any, key: string = "SYSUPTIME"): string {
        if (!dataSource?.connected || !dataSource[key]) return "-- Hr -- Min";

        let seconds = parseInt(dataSource[key]);
        let days = Math.floor(seconds / 86400);
        let hours = Math.floor((seconds % 86400) / 3600);
        let minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) {
            return `${days} D ${hours} Hr ${minutes} Min`;
        }

        return `${hours} Hr ${minutes} Min`;
    }

    /**
     * Create random string
     */
    public getRandomString(length: number): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';

        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return result;
    }

    /**
      * Get date with mutation
      */
    public getDateWithMutation(days: number): Date {
        const date = new Date();
        date.setDate(date.getDate() + days);
        return date;
    }

    /**
     * Parse permission into group
     */
    public parsingPermissionGroup(permission: string): string {
        const permissionGroups = {
            command: [
                RolePermission.COMMAND_REBOOT,
                RolePermission.COMMAND_RESTORE,
                RolePermission.COMMAND_UPGRADE_FW,
                RolePermission.COMMAND_UPGRADE_CFG,
                RolePermission.COMMAND_BACKUP_CFG,
                RolePermission.COMMAND_DIAGNOSTIC,
                RolePermission.COMMAND_CBSD
            ],
            device: [
                RolePermission.DEVICE_CFG,
                RolePermission.DEVICE_ANALYSIS,
                RolePermission.DEVICE_EDIT,
                RolePermission.DEVICE_REMOTE_ACCESS,
                RolePermission.DEVICE_DELETE
            ],
            view: [
                RolePermission.VIEW_ANALYTICS,
                RolePermission.VIEW_CELL_ANALYSIS,
                RolePermission.VIEW_SCHEDULER,
                RolePermission.VIEW_PROFILE_TEMPLATE,
                RolePermission.VIEW_FILE_MANAGEMENT,
                RolePermission.VIEW_REPORT,
                RolePermission.VIEW_MAP
            ],
            feature: [
                RolePermission.FEATURES_ALERT,
                RolePermission.FEATURES_REST_API,
                RolePermission.FEATURES_WEBHOOK
            ],
            config: [
                RolePermission.CONFIG_INTERFACE,
                RolePermission.CONFIG_DUAL_WAN,
                RolePermission.CONFIG_WIFI,
                RolePermission.CONFIG_ACCESS_MANAGEMENT,
                RolePermission.CONFIG_MAINTENANCE,
                RolePermission.CONFIG_ADVANCED_SETUP,
                RolePermission.CONFIG_VPN
            ]
        };

        for (const [group, permissions] of Object.entries(permissionGroups)) {
            if ((permissions as string[]).includes(permission)) {
                return group.charAt(0).toUpperCase() + group.slice(1);
            }
        }

        return "";
    }

    /**
     * Get all dirty form values recursively
     */
    public getDirtyValues(form: UntypedFormGroup): any {
        const dirtyValues = {};

        Object.keys(form.controls).forEach(key => {
            const control = form.get(key);
            if (control?.dirty && control.status !== 'DISABLED') {
                if (control.hasOwnProperty('controls')) {
                    dirtyValues[key] = this.getDirtyValues(control as UntypedFormGroup);
                } else {
                    dirtyValues[key] = control.value;
                }
            }
        });

        return dirtyValues;
    }

    /**
     * Validate BEC MAC address
     */
    public validateBECMacAddress(testInput: string): string {
        if (!testInput) return "MAC can not be empty.";
        const format = MAC_ADDRESS_REGEX.test(testInput.toUpperCase());
        return format ? "" : "Incorrect MAC Format.";
    }

    /**
     * Validate URL
     */
    public validateURL(testInput: string): string {
        if (!testInput) return "url can not be empty.";
        const format = URL_REGEX.test(testInput) || FTP_REGEX.test(testInput);
        return format ? "" : "Incorrect URL/FTP Format.";
    }

    /**
     * Parse IPv4 address
     */
    public parseIpv4(testString: string): Array<string> | null {
        if (!IPV4_REGEX.test(testString)) return null;

        const [num1, num2, num3, num4] = testString.split('.');
        const octets = [num1, num2, num3, num4].map(n => parseInt(n));

        if (octets.some(n => n > 255)) return null;
        return [num1, num2, num3, num4];
    }

    /**
     * Resolve license bucket name
     */
    public toLicenseBucketName(licenseCount: number): string {
        if (!licenseCount) return "";
        const bucket = licenseCount < 1000 ? licenseCount.toString() : ((licenseCount / 1000) + "K");
        return `ISP-MO-${bucket}LP`;
    }

    /**
     * Convert value to MB
     */
    public convertToMB(data: number): string {
        const mb = (Math.floor((data / 1048576) * 100) * 0.01);
        return mb.toFixed(2);
    }

    /**
     * Render alert description
     */
    public alertDescription(
        alertType: DeviceAlertType,
        value: string | number,
        alertLevel: "Alert" | "Caution"
    ): string {
        const signalTypes = [
            DeviceAlertType.RSRP,
            DeviceAlertType.RSRQ,
            DeviceAlertType.SINR,
            DeviceAlertType.RSSI,
            DeviceAlertType.NR_RSRP,
            DeviceAlertType.NR_RSRQ,
            DeviceAlertType.NR_SINR
        ];

        if (signalTypes.includes(alertType)) {
            return `${value} dBm have been lower than the ${alertLevel.toLowerCase()} value.`;
        }

        if (alertType === DeviceAlertType.TEMPERATURE) {
            return `${value} °C have been higher than the ${alertLevel.toLowerCase()} value.`;
        }

        return value.toString();
    }

    /**
     * Convert date string to ISO format
     */
    public convertToISOString(dateString: string, timezone: TimeZone): string {
        const timeZoneOffset = DateTime.now().setZone(timezone).offset;
        const offset = timeZoneOffset * 60 * 1000;
        const date = new Date(Date.parse(dateString) - offset);

        return `${date.getFullYear()}-${this.toTwoDigit(date.getMonth() + 1)}-${this.toTwoDigit(date.getDate())}` +
            `T${this.toTwoDigit(date.getHours())}:${this.toTwoDigit(date.getMinutes())}:00.000Z`;
    }

    /**
     * Get current time string
     */
    public getCurrentTimeString(): string {
        const date = new Date();
        return `${date.getFullYear()}${this.toTwoDigit(date.getMonth() + 1)}${this.toTwoDigit(date.getDate())}` +
            `T${this.toTwoDigit(date.getHours())}${this.toTwoDigit(date.getMinutes())}`;
    }

    /**
     * Request fullscreen for element
     */
    public requestElementFullScreen(element: HTMLElement): void {
        const method = element.requestFullscreen || (element as any).webkitRequestFullscreen || (element as any).msRequestFullscreen;
        method?.call(element);
    }

    /**
     * Exit fullscreen for element
     */
    public exitElementFullScreen(): void {
        const method = (this._document as any).exitFullscreen || (this._document as any).webkitExitFullscreen || (this._document as any).msExitFullscreen;
        method?.call(this._document);
    }

    /**
     * Convert HTTP event to progress object
     */
    public getEventMessage(event: HttpEvent<any>): FileTransferingProgress | any {
        switch (event.type) {
            case HttpEventType.Sent:
                return { progress: 0, state: FileStage.IN_PROGRESS };
            case HttpEventType.UploadProgress:
                const percent = event.total ? Math.round(100 * event.loaded / event.total) : 0;
                const state = event.loaded === event.total ? FileStage.UPDATE : FileStage.IN_PROGRESS;
                return { progress: percent, state };
            case HttpEventType.Response:
                return event.body;
            default:
                return "";
        }
    }

    /**
     * Calculate GNB ID and Sector ID from NCI
     */
    public calculateGnbId(nciInHex: string, gNbIdLength: number): Array<string> {
        let binary = parseInt(nciInHex, 16).toString(2);
        const padding = Array(36 - binary.length).fill(0).join('');
        const finalString = `${padding}${binary}`;

        return [
            parseInt(finalString.substring(0, gNbIdLength), 2).toString(10),
            parseInt(finalString.substring(gNbIdLength), 2).toString(10)
        ];
    }

    /**
     * Run function after frame flush
     */
    public runAfterAnotherFrame(fn: Function): void {
        setTimeout(fn, 0);
    }

    /**
     * Get MIME type from file header
     */
    public getMimeType(file: File, bytePattern: any, callback?: Function): void {
        const reader = new FileReader();

        reader.onload = () => {
            const arr = new Uint8Array(reader.result as ArrayBuffer);
            let type = "unknown";

            for (const key in bytePattern) {
                const length = key.length / 2;
                const tempArray = arr.subarray(0, length);
                let header = "";

                for (let i = 0; i < tempArray.length; i++) {
                    header += tempArray[i].toString(16);
                }

                if (header.toUpperCase() === key) {
                    type = bytePattern[key];
                    break;
                }
            }

            callback?.(type);
        };

        reader.readAsArrayBuffer(file);
    }

    /**
     * Get firmware/configuration MIME type
     */
    public getFwCfgMimeType(file: File, fileType: 'firmware' | 'configuration', callback: Function): void {
        const reader = new FileReader();

        reader.onload = () => {
            const arr = new Uint8Array(reader.result as ArrayBuffer);
            const tempArray = arr.subarray(0, 256);
            let header = "";

            for (let i = 0; i < tempArray.length; i++) {
                const hex = tempArray[i].toString(16);
                header += hex.length < 2 ? `0${hex}` : hex;
            }

            const fileNameExt = file.name.split('.').pop();
            const type = this.platformConfig.encodeFileCheck
                ? this.encodeFileCheck(fileType, header)
                : this.standardFileCheck(fileType, header, fileNameExt);

            callback(type);
        };

        reader.readAsArrayBuffer(file);
    }

    /**
     * Clear authentication cookie
     */
    public clearCookie(): void {
        this._document.cookie = `username=;Max-Age=0;path=/;`;
    }

    /**
     * Check form differences
     */
    public checkDifference(before: any, after: any): any {
        const keySet = new Set([...Object.keys(before), ...Object.keys(after)]);
        const differentObject = {};

        keySet.forEach(key => {
            if (before.hasOwnProperty(key) && after.hasOwnProperty(key)) {
                if (typeof after[key] === 'object') {
                    if (!_.isEqual(before[key], after[key])) {
                        differentObject[key] = !Array.isArray(after[key])
                            ? this.checkDifference(before[key], after[key])
                            : after[key];
                    }
                } else if (before[key] !== after[key]) {
                    differentObject[key] = after[key] ?? "<REMOVE>";
                }
            } else {
                differentObject[key] = after[key] ?? "<REMOVE>";
            }
        });

        return differentObject;
    }

    /**
     * Replace special characters for module upgrade
     */
    public replaceSpecialChar(template: string, inputString: string): string {
        const escaped = inputString.replace(/&/gi, '\\&');
        return template.replace('<URL>', escaped);
    }
}